import Dexie from 'dexie'
import encryptionService from './encryptionService'

class OfflineStorage {
  constructor () {
    this.db = new Dexie('LSGOfflineDB')
    this.currentUserId = null // Set on login for user-level data isolation

    // Define database schema - Version 2 adds schatting tables
    this.db.version(1).stores({
      pendingRequests: '++id, url, method, timestamp, status',
      cachedData: 'key, data, timestamp'
    })

    this.db.version(2).stores({
      pendingRequests: '++id, url, method, timestamp, status',
      cachedData: 'key, data, timestamp',
      // Schatting step stores
      schattingData: 'idSchatting, userId, timestamp, syncStatus',
      algemeneGegevens: 'idSchatting, timestamp, syncStatus',
      afmetingen: 'idSchatting, timestamp, syncStatus',
      beschrijvingPand: 'idSchatting, timestamp, syncStatus',
      bouwmateriaal: 'idSchatting, timestamp, syncStatus',
      nutsvoorzieningComfort: 'idSchatting, timestamp, syncStatus',
      waarde: 'idSchatting, timestamp, syncStatus',
      vergelijkingspunten: 'idSchatting, timestamp, syncStatus',
      bijzonderElement: 'idSchatting, timestamp, syncStatus',
      // Metadata and reference data
      schattingMetadata: 'idSchatting, lastSync, cacheExpiry, stepsComplete',
      referenceData: 'type, timestamp, data',
      // For dropdown data (banken, bebouwingen, gebruikers, postcodes, etc.)
      lookupData: 'type, timestamp, data'
    })

    this.db.version(3).stores({
      pendingRequests: '++id, url, method, timestamp, status',
      cachedData: 'key, data, timestamp',
      // Schatting step stores
      schattingData: 'idSchatting, userId, timestamp, syncStatus',
      algemeneGegevens: 'idSchatting, timestamp, syncStatus',
      afmetingen: 'idSchatting, timestamp, syncStatus',
      beschrijvingPand: 'idSchatting, timestamp, syncStatus',
      bouwmateriaal: 'idSchatting, timestamp, syncStatus',
      nutsvoorzieningComfort: 'idSchatting, timestamp, syncStatus',
      waarde: 'idSchatting, timestamp, syncStatus',
      vergelijkingspunten: 'idSchatting, timestamp, syncStatus',
      bijzonderElement: 'idSchatting, timestamp, syncStatus',
      // Schatting code selecties per step
      bijzonderElementCodeSelecties: 'idSchatting, timestamp, syncStatus',
      bouwmateriaalCodeSelecties: 'idSchatting, timestamp, syncStatus',
      nutsvoorzieningComfortCodeSelecties: 'idSchatting, timestamp, syncStatus',
      beschrijvingPandCodeSelecties: 'idSchatting, timestamp, syncStatus',
      // Metadata and reference data
      schattingMetadata: 'idSchatting, lastSync, cacheExpiry, stepsComplete',
      referenceData: 'type, timestamp, data',
      // For dropdown data (banken, bebouwingen, gebruikers, postcodes, etc.)
      lookupData: 'type, timestamp, data'
    })

    this.db.version(4).stores({
      pendingRequests: '++id, url, method, timestamp, status, baseUrl',
      cachedData: 'key, data, timestamp',
      // File upload queue
      fileUploads: '++id, idKoppeling, type, uploadStatus, timestamp, retryCount',
      // Schatting step stores
      schattingData: 'idSchatting, userId, timestamp, syncStatus',
      algemeneGegevens: 'idSchatting, timestamp, syncStatus',
      afmetingen: 'idSchatting, timestamp, syncStatus',
      beschrijvingPand: 'idSchatting, timestamp, syncStatus',
      bouwmateriaal: 'idSchatting, timestamp, syncStatus',
      nutsvoorzieningComfort: 'idSchatting, timestamp, syncStatus',
      waarde: 'idSchatting, timestamp, syncStatus',
      vergelijkingspunten: 'idSchatting, timestamp, syncStatus',
      bijzonderElement: 'idSchatting, timestamp, syncStatus',
      // Schatting code selecties per step
      bijzonderElementCodeSelecties: 'idSchatting, timestamp, syncStatus',
      bouwmateriaalCodeSelecties: 'idSchatting, timestamp, syncStatus',
      nutsvoorzieningComfortCodeSelecties: 'idSchatting, timestamp, syncStatus',
      beschrijvingPandCodeSelecties: 'idSchatting, timestamp, syncStatus',
      // Metadata and reference data
      schattingMetadata: 'idSchatting, lastSync, cacheExpiry, stepsComplete',
      referenceData: 'type, timestamp, data',
      // For dropdown data (banken, bebouwingen, gebruikers, postcodes, etc.)
      lookupData: 'type, timestamp, data'
    })

    // Store initialization promise
    this.isReady = this.db.open().then(() => {
      console.log(`IndexedDB opened successfully - Version: ${this.db.verno}`)
      return true
    }).catch((err) => {
      console.error('Failed to open IndexedDB:', err)
      throw err
    })

    this.pendingRequests = this.db.pendingRequests
    this.cachedData = this.db.cachedData
    this.fileUploads = this.db.fileUploads
    // Add references to new stores
    this.schattingData = this.db.schattingData
    this.algemeneGegevens = this.db.algemeneGegevens
    this.afmetingen = this.db.afmetingen
    this.beschrijvingPand = this.db.beschrijvingPand
    this.bouwmateriaal = this.db.bouwmateriaal
    this.nutsvoorzieningComfort = this.db.nutsvoorzieningComfort
    this.waarde = this.db.waarde
    this.vergelijkingspunten = this.db.vergelijkingspunten
    this.bijzonderElement = this.db.bijzonderElement
    this.schattingMetadata = this.db.schattingMetadata
    this.referenceData = this.db.referenceData
    this.lookupData = this.db.lookupData
    this.bijzonderElementCodeSelecties = this.db.bijzonderElementCodeSelecties
    this.bouwmateriaalCodeSelecties = this.db.bouwmateriaalCodeSelecties
    this.nutsvoorzieningComfortCodeSelecties = this.db.nutsvoorzieningComfortCodeSelecties
    this.beschrijvingPandCodeSelecties = this.db.beschrijvingPandCodeSelecties
  }

  // Add a request to the queue
  async addRequest (requestData) {
    return await this.pendingRequests.add({
      ...requestData,
      timestamp: Date.now(),
      status: 'pending'
    })
  }

  // Get all pending requests
  async getAllRequests () {
    return await this.pendingRequests
      .where('status')
      .equals('pending')
      .toArray()
  }

  // Get requests by status
  async getRequestsByStatus (status) {
    return await this.pendingRequests.where('status').equals(status).toArray()
  }

  // Update request status
  async updateRequestStatus (id, status) {
    return await this.pendingRequests.update(id, {
      status,
      lastAttempt: Date.now()
    })
  }

  // Delete a specific request
  async deleteRequest (id) {
    return await this.pendingRequests.delete(id)
  }

  // Clear all pending requests
  async clearPendingRequests () {
    return await this.pendingRequests
      .where('status')
      .equals('pending')
      .delete()
  }

  // Cache data for offline use (with encryption)
  async cacheData (key, data) {
    const encryptedData = await this.encryptData(data)
    return await this.cachedData.put({
      key,
      ...encryptedData,
      timestamp: Date.now()
    })
  }

  // Get cached data (with decryption)
  async getCachedData (key) {
    const storedData = await this.cachedData.get(key)
    if (!storedData) return null
    return await this.decryptData(storedData)
  }

  // Clear old cached data (older than specified days)
  async clearOldCache (days = 7) {
    const cutoffTime = Date.now() - days * 24 * 60 * 60 * 1000
    return await this.cachedData.where('timestamp').below(cutoffTime).delete()
  }

  // Get statistics
  async getStatistics () {
    const pending = await this.pendingRequests
      .where('status')
      .equals('pending')
      .count()

    const failed = await this.pendingRequests
      .where('status')
      .equals('failed')
      .count()

    const synced = await this.pendingRequests
      .where('status')
      .equals('synced')
      .count()

    return { pending, failed, synced }
  }

  // Bulk operations for better performance
  async bulkAddRequests (requests) {
    const requestsWithMetadata = requests.map((req) => ({
      ...req,
      timestamp: Date.now(),
      status: 'pending'
    }))
    return await this.pendingRequests.bulkAdd(requestsWithMetadata)
  }

  // Transaction support for complex operations
  async transaction (callback) {
    return await this.db.transaction(
      'rw',
      this.pendingRequests,
      this.cachedData,
      callback
    )
  }

  // ============ SCHATTING-SPECIFIC METHODS ============

  // Save a specific schatting step (with encryption and userId)
  async saveSchattingStep (stepType, idSchatting, data) {
    const storeMap = {
      algemeneGegevens: this.algemeneGegevens,
      afmetingen: this.afmetingen,
      beschrijvingPand: this.beschrijvingPand,
      bouwmateriaal: this.bouwmateriaal,
      nutsvoorzieningComfort: this.nutsvoorzieningComfort,
      waarde: this.waarde,
      vergelijkingspunten: this.vergelijkingspunten,
      bijzonderElement: this.bijzonderElement,
      bijzonderElementCodeSelecties: this.bijzonderElementCodeSelecties,
      bouwmateriaalCodeSelecties: this.bouwmateriaalCodeSelecties,
      nutsvoorzieningComfortCodeSelecties: this.nutsvoorzieningComfortCodeSelecties,
      beschrijvingPandCodeSelecties: this.beschrijvingPandCodeSelecties
    }

    const store = storeMap[stepType]
    if (!store) {
      throw new Error(`Unknown step type: ${stepType}`)
    }

    // Encrypt sensitive data before storing (do NOT include userId in encrypted data)
    const encryptedData = await this.encryptData({
      ...data,
      timestamp: Date.now(),
      syncStatus: 'cached'
    })

    // Store userId as metadata (outside encrypted data) for access control
    return await store.put({
      idSchatting,
      userId: this.currentUserId, // Store userId separately for validation
      ...encryptedData
    })
  }

  // Get a specific schatting step (with decryption and userId validation)
  async getSchattingStep (stepType, idSchatting) {
    const storeMap = {
      algemeneGegevens: this.algemeneGegevens,
      afmetingen: this.afmetingen,
      beschrijvingPand: this.beschrijvingPand,
      bouwmateriaal: this.bouwmateriaal,
      nutsvoorzieningComfort: this.nutsvoorzieningComfort,
      waarde: this.waarde,
      vergelijkingspunten: this.vergelijkingspunten,
      bijzonderElement: this.bijzonderElement,
      bijzonderElementCodeSelecties: this.bijzonderElementCodeSelecties,
      bouwmateriaalCodeSelecties: this.bouwmateriaalCodeSelecties,
      nutsvoorzieningComfortCodeSelecties: this.nutsvoorzieningComfortCodeSelecties,
      beschrijvingPandCodeSelecties: this.beschrijvingPandCodeSelecties
    }

    const store = storeMap[stepType]
    if (!store) {
      throw new Error(`Unknown step type: ${stepType}`)
    }

    const storedData = await store.get(idSchatting)
    if (!storedData) return null

    // Validate userId BEFORE decryption - only decrypt if it belongs to current user
    if (storedData.userId && storedData.userId !== this.currentUserId) {
      console.warn(`Data access denied: userId mismatch for ${stepType}`)
      return null
    }

    // Decrypt the data
    const decryptedData = await this.decryptData(storedData)

    // Check if data is still valid
    if (decryptedData && this.isDataValid(decryptedData)) {
      return decryptedData
    }
    return null
  }

  // Prefetch all schatting steps at once
  async prefetchAllSchattingSteps (idSchatting, schattingService) {
    try {
      // Fetch all data in parallel
      const [
        schatting,
        algemeneGegevens,
        afmetingen,
        beschrijvingPand,
        bouwmateriaal,
        nutsvoorzieningComfort,
        waarde,
        vergelijkingspunten,
        bijzonderElement
      ] = await Promise.all([
        schattingService.GetSchattingById(idSchatting),
        schattingService.GetAlgemeneGegevensByIdSchatting(idSchatting),
        schattingService.GetAfmetingenByIdSchatting(idSchatting),
        schattingService.GetBeschrijvingPandByIdSchatting(idSchatting),
        schattingService.GetBouwmateriaalByIdSchatting(idSchatting),
        schattingService.GetNutsvoorzieningComfortByIdSchatting(idSchatting),
        schattingService.GetWaardeByIdSchatting(idSchatting),
        schattingService.GetVergelijkingspuntenByIdSchatting(idSchatting),
        schattingService.GetBijzonderElementByIdSchatting(idSchatting)
      ])

      // Save all data to IndexedDB
      const savePromises = []

      if (schatting?.data) {
        savePromises.push(this.schattingData.put({
          ...schatting.data,
          idSchatting,
          timestamp: Date.now(),
          syncStatus: 'cached'
        }))
      }

      const stepsToSave = [
        { type: 'algemeneGegevens', data: algemeneGegevens?.data },
        { type: 'afmetingen', data: afmetingen?.data },
        { type: 'beschrijvingPand', data: beschrijvingPand?.data },
        { type: 'bouwmateriaal', data: bouwmateriaal?.data },
        { type: 'nutsvoorzieningComfort', data: nutsvoorzieningComfort?.data },
        { type: 'waarde', data: waarde?.data },
        { type: 'vergelijkingspunten', data: vergelijkingspunten?.data },
        { type: 'bijzonderElement', data: bijzonderElement?.data }
      ]

      for (const step of stepsToSave) {
        if (step.data) {
          savePromises.push(this.saveSchattingStep(step.type, idSchatting, step.data))
        }
      }

      // Save metadata
      savePromises.push(this.schattingMetadata.put({
        idSchatting,
        lastSync: Date.now(),
        cacheExpiry: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        stepsComplete: stepsToSave.filter(s => s.data).map(s => s.type)
      }))

      await Promise.all(savePromises)

      return {
        success: true,
        cached: stepsToSave.filter(s => s.data).map(s => s.type)
      }
    } catch (error) {
      console.error('Error prefetching schatting data:', error)
      return { success: false, error: error.message }
    }
  }

  // Get all cached steps for a schatting
  async getAllSchattingSteps (idSchatting) {
    const steps = [
      'algemeneGegevens',
      'afmetingen',
      'beschrijvingPand',
      'bouwmateriaal',
      'nutsvoorzieningComfort',
      'waarde',
      'vergelijkingspunten',
      'bijzonderElement'
    ]

    const result = {}
    for (const step of steps) {
      result[step] = await this.getSchattingStep(step, idSchatting)
    }

    return result
  }

  // Save lookup/reference data (banken, bebouwingen, etc.)
  async saveLookupData (type, data) {
    return await this.lookupData.put({
      type,
      data,
      timestamp: Date.now()
    })
  }

  // Get lookup/reference data
  async getLookupData (type) {
    const result = await this.lookupData.get(type)
    if (result && this.isDataValid(result)) {
      return result.data
    }
    return null
  }

  // Prefetch all lookup data needed for the form
  async prefetchLookupData (services) {
    try {
      const lookupPromises = []

      // Define what lookup data to fetch
      const lookupTypes = [
        { type: 'banken', fetcher: () => services.codeService.GetBanken() },
        { type: 'bebouwingen', fetcher: () => services.codeService.GetBebouwingen() },
        { type: 'gebruikers', fetcher: () => services.authenticationService.GetGebruikersList() },
        { type: 'postcodes', fetcher: () => services.postService.GetPosten() }
      ]

      // Fetch and save all lookup data
      for (const lookup of lookupTypes) {
        lookupPromises.push(
          lookup.fetcher()
            .then(response => {
              if (response?.data) {
                return this.saveLookupData(lookup.type, response.data)
              }
            })
            .catch(error => {
              console.warn(`Failed to fetch ${lookup.type}:`, error)
            })
        )
      }

      await Promise.all(lookupPromises)
      return { success: true }
    } catch (error) {
      console.error('Error prefetching lookup data:', error)
      return { success: false, error: error.message }
    }
  }

  // Check if cached data is still valid
  isDataValid (data, maxAgeMs = 24 * 60 * 60 * 1000) {
    if (!data || !data.timestamp) return false
    const age = Date.now() - data.timestamp
    return age < maxAgeMs
  }

  // Get metadata for a schatting
  async getSchattingMetadata (idSchatting) {
    return await this.schattingMetadata.get(idSchatting)
  }

  // Clear all schatting data for a specific ID
  async clearSchattingData (idSchatting) {
    const stores = [
      this.schattingData,
      this.algemeneGegevens,
      this.afmetingen,
      this.beschrijvingPand,
      this.bouwmateriaal,
      this.nutsvoorzieningComfort,
      this.waarde,
      this.vergelijkingspunten,
      this.bijzonderElement,
      this.schattingMetadata
    ]

    const deletePromises = stores.map(store =>
      store.where('idSchatting').equals(idSchatting).delete()
    )

    await Promise.all(deletePromises)
  }

  // Clear expired schatting data
  async clearExpiredSchattingData () {
    const cutoffTime = Date.now() - (24 * 60 * 60 * 1000) // 24 hours

    const stores = [
      this.schattingData,
      this.algemeneGegevens,
      this.afmetingen,
      this.beschrijvingPand,
      this.bouwmateriaal,
      this.nutsvoorzieningComfort,
      this.waarde,
      this.vergelijkingspunten,
      this.bijzonderElement
    ]

    const deletePromises = stores.map(store =>
      store.where('timestamp').below(cutoffTime).delete()
    )

    // Also clear expired metadata
    deletePromises.push(
      this.schattingMetadata.where('cacheExpiry').below(Date.now()).delete()
    )

    await Promise.all(deletePromises)
  }

  // Queue a schatting update for sync
  async queueSchattingUpdate (idSchatting, stepType, data, method = 'PUT') {
    const url = `/api/Schatting/UpdateSchatting${stepType.charAt(0).toUpperCase() + stepType.slice(1)}`

    return await this.addRequest({
      url,
      method,
      data,
      idSchatting,
      stepType,
      timestamp: Date.now(),
      status: 'pending'
    })
  }

  // Check if we have complete offline data for a schatting
  async hasCompleteOfflineData (idSchatting) {
    const metadata = await this.getSchattingMetadata(idSchatting)
    if (!metadata || !this.isDataValid({ timestamp: metadata.lastSync })) {
      return false
    }

    const requiredSteps = [
      'algemeneGegevens',
      'afmetingen',
      'beschrijvingPand',
      'bouwmateriaal',
      'nutsvoorzieningComfort',
      'waarde',
      'vergelijkingspunten',
      'bijzonderElement'
    ]

    return requiredSteps.every(step => metadata.stepsComplete.includes(step))
  }

  // ============ FILE UPLOAD QUEUE METHODS ============

  // Add file to upload queue
  async addFileUpload (fileData) {
    await this.isReady // Ensure database is ready
    return await this.fileUploads.add({
      ...fileData,
      timestamp: Date.now(),
      uploadStatus: 'pending',
      retryCount: 0,
      uploadProgress: 0
    })
  }

  // Get all pending file uploads
  async getPendingFileUploads () {
    return await this.fileUploads
      .where('uploadStatus')
      .equals('pending')
      .toArray()
  }

  // Get file uploads by status
  async getFileUploadsByStatus (status) {
    return await this.fileUploads
      .where('uploadStatus')
      .equals(status)
      .toArray()
  }

  // Get file uploads by idKoppeling (e.g., idSchatting)
  async getFileUploadsByKoppeling (idKoppeling) {
    return await this.fileUploads
      .where('idKoppeling')
      .equals(idKoppeling)
      .toArray()
  }

  // Update file upload status
  async updateFileUploadStatus (id, status, additionalData = {}) {
    return await this.fileUploads.update(id, {
      uploadStatus: status,
      lastUpdated: Date.now(),
      ...additionalData
    })
  }

  // Update file upload progress
  async updateFileUploadProgress (id, progress) {
    return await this.fileUploads.update(id, {
      uploadProgress: progress,
      lastUpdated: Date.now()
    })
  }

  // Delete file upload record
  async deleteFileUpload (id) {
    return await this.fileUploads.delete(id)
  }

  // Get upload statistics
  async getFileUploadStats () {
    const pending = await this.fileUploads.where('uploadStatus').equals('pending').count()
    const uploading = await this.fileUploads.where('uploadStatus').equals('uploading').count()
    const completed = await this.fileUploads.where('uploadStatus').equals('completed').count()
    const failed = await this.fileUploads.where('uploadStatus').equals('failed').count()

    return { pending, uploading, completed, failed }
  }

  // Clean up completed uploads
  async cleanupCompletedUploads (olderThanMs = 24 * 60 * 60 * 1000) {
    const cutoffTime = Date.now() - olderThanMs
    return await this.fileUploads
      .where('uploadStatus')
      .equals('completed')
      .and(upload => upload.lastUpdated && upload.lastUpdated < cutoffTime)
      .delete()
  }

  // ============ DATABASE INFO METHODS ============

  /**
   * Get database information
   * @returns {Promise<Object>} Database version and table info
   */
  async getDatabaseInfo () {
    await this.isReady
    const tables = this.db.tables.map(table => table.name)
    return {
      version: this.db.verno,
      name: this.db.name,
      tables,
      isOpen: this.db.isOpen()
    }
  }

  /**
   * Ensure database is ready before operations
   * @returns {Promise<boolean>}
   */
  async ensureReady () {
    return await this.isReady
  }

  // ============ SECURITY METHODS ============

  /**
   * Set current user ID for data isolation
   * @param {string|number} userId - The current user's ID
   */
  setCurrentUserId (userId) {
    this.currentUserId = userId
    console.log(`IndexedDB scoped to userId: ${userId}`)
  }

  /**
   * Clear current user ID (call on logout)
   */
  clearCurrentUserId () {
    this.currentUserId = null
  }

  /**
   * Get current user ID
   * @returns {string|number|null}
   */
  getCurrentUserId () {
    return this.currentUserId
  }

  /**
   * Clear all data from IndexedDB (call on logout)
   * Removes all cached data but keeps database structure
   */
  async clearAllData () {
    try {
      await Promise.all([
        this.pendingRequests.clear(),
        this.cachedData.clear(),
        this.fileUploads.clear(),
        this.schattingData.clear(),
        this.algemeneGegevens.clear(),
        this.afmetingen.clear(),
        this.beschrijvingPand.clear(),
        this.bouwmateriaal.clear(),
        this.nutsvoorzieningComfort.clear(),
        this.waarde.clear(),
        this.vergelijkingspunten.clear(),
        this.bijzonderElement.clear(),
        this.bijzonderElementCodeSelecties.clear(),
        this.bouwmateriaalCodeSelecties.clear(),
        this.nutsvoorzieningComfortCodeSelecties.clear(),
        this.beschrijvingPandCodeSelecties.clear(),
        this.schattingMetadata.clear(),
        this.referenceData.clear(),
        this.lookupData.clear()
      ])
      console.log('All IndexedDB data cleared successfully')
      return true
    } catch (error) {
      console.error('Error clearing IndexedDB:', error)
      throw error
    }
  }

  async deleteDatabase () {
    try {
      await this.db.delete()
      console.log('IndexedDB database deleted successfully')
      return true
    } catch (error) {
      console.error('Error deleting database:', error)
      throw error
    }
  }

  // ============ ENCRYPTION METHODS ============
  async encryptData (data) {
    if (!encryptionService.isAvailable()) {
      // If encryption is not available, store unencrypted (fallback for backward compatibility)
      console.warn('Encryption not available, storing data unencrypted')
      return { encrypted: false, data }
    }

    try {
      const encryptedData = await encryptionService.encrypt(data)
      return { encrypted: true, data: encryptedData }
    } catch (error) {
      console.error('Encryption failed, storing unencrypted:', error)
      return { encrypted: false, data }
    }
  }

  async decryptData (storedData) {
    if (!storedData) return null

    // Check if data was encrypted
    if (storedData.encrypted) {
      try {
        return await encryptionService.decrypt(storedData.data)
      } catch (error) {
        console.error('Decryption failed:', error)
        throw new Error('Failed to decrypt data. Please log in again.')
      }
    }

    // Return unencrypted data as-is (backward compatibility)
    return storedData.data || storedData
  }
}

export default new OfflineStorage()
